import React from 'react'
import { match } from 'ts-pattern'

import { AnswerOptionMultiSelector } from '~/components/features/AnswerOptionMultiSelector'
import {
  AnswerOptionSelector,
  type AnswerOptionSelectorProps,
} from '~/components/features/AnswerOptionSelector'
import {
  type AnswerOption as AnswerOptionBase,
  type ConditionOperation,
} from '~/gql/graphql'
import { unassignedSymbol } from '~/utils'

type AnswerOption = App.PickGraphQL<AnswerOptionBase, 'option' | 'text'>

export type AnswerOptionConditionalSelectorProps<
  TAnswerOption extends AnswerOption,
> = {
  answerOptions: TAnswerOption[]
  className?: string
  conditionOperation: ConditionOperation
  disabled?: boolean
  onBlur?: () => void
  onChange?: (options: TAnswerOption['option'][]) => void
  value: TAnswerOption['option'][]
}

export const AnswerOptionConditionalSelector = React.forwardRef(
  <TAnswerOption extends AnswerOption>(
    {
      answerOptions,
      className,
      conditionOperation,
      disabled,
      onBlur,
      onChange,
      value,
    }: AnswerOptionConditionalSelectorProps<TAnswerOption>,
    ref: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const handleAnswerOptionChange = React.useCallback<
      EH<AnswerOptionSelectorProps<TAnswerOption>, 'onChange'>
    >(
      (option) => {
        if (option !== unassignedSymbol) {
          onChange?.([option])
        }
      },
      [onChange],
    )

    return (
      <>
        {match(conditionOperation)
          .with('equal', 'not_equal', () => (
            <AnswerOptionSelector
              ref={ref}
              answerOptions={answerOptions}
              className={className}
              disabled={disabled}
              value={value[0]}
              onBlur={onBlur}
              onChange={handleAnswerOptionChange}
            />
          ))
          .with('in', 'not_in', () => (
            <AnswerOptionMultiSelector
              ref={ref}
              answerOptions={answerOptions}
              className={className}
              disabled={disabled}
              value={value}
              onBlur={onBlur}
              onChange={onChange}
            />
          ))
          .exhaustive()}
      </>
    )
  },
)
